import { Map, View } from 'ol'
import Bounce from 'ol-ext/featureanimation/Bounce'
import { Tile as TileLayer } from 'ol/layer'
import { OSM } from 'ol/source'
import { fromLonLat, transform, transformExtent, get } from 'ol/proj'
import { boundingExtent } from 'ol/extent'
import Point from 'ol/geom/Point'
import Feature from 'ol/Feature'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import Overlay from 'ol/Overlay'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Icon from 'ol/style/Icon'
import bezier from '@turf/bezier-spline'
import GeoJSON from 'ol/format/GeoJSON'
import { Circle as CircleStyle } from 'ol/style.js'
import { MAP_SETTINGS } from '@/configs/constants'

let moveFeatureCallback = null

export default {
  buildMap () {
    const tileLayer = new TileLayer({
      source: new OSM({
        url: `${process.env.VUE_APP_TILES_SERVER_HOST}/tile/{z}/{x}/{y}.png`,
        crossOrigin: null
      })
    })

    const view = new View({
      center: fromLonLat([MAP_SETTINGS.defaultCenter.longitude, MAP_SETTINGS.defaultCenter.latitude]),
      zoom: MAP_SETTINGS.defaultZoom,
      minZoom: MAP_SETTINGS.minZoom,
      maxZoom: MAP_SETTINGS.maxZoom
    })

    return new Map({
      target: 'map',
      layers: [tileLayer],
      view
    })
  },

  buildHubsLayer (packagePayload, state) {
    const features = []

    packagePayload.previousSteps.forEach(step => {
      features.push(this.buildHubFeature(step, require('@/assets/images/markers/map-previous-hub.png')))
    })

    packagePayload.futureSteps.forEach(step => {
      features.push(this.buildHubFeature(step, require('@/assets/images/markers/map-future-hub.png')))
    })

    if (packagePayload.currentStep && packagePayload.currentStep.info.type === 'HUB') {
      features.push(this.buildHubFeature(packagePayload.currentStep, require('@/assets/images/markers/map-current-hub.png')))
    }

    let hubs = new VectorLayer({ source: new VectorSource({ features }) })
    this.addBounceHubAnimation(hubs, state.map)
    return hubs
  },

  buildHubFeature (step, imageSrc) {
    const address = step.info.address
    const point = new Point(fromLonLat([address.longitude, address.latitude]))
    return this.buildMarkerFeature(point, imageSrc, step)
  },

  buildMarkerFeature (point, imageSrc, step) {
    let feature = new Feature({ geometry: point, step })
    feature.setStyle(new Style({
      image: new Icon(({
        src: imageSrc
      }))
    }))
    return feature
  },

  buildPreviousStepsPath (packagePayload) {
    if (!this.hasPreviousPath(packagePayload)) {
      return new VectorLayer()
    }
    const steps = this.getPreviousPathSteps(packagePayload)

    const style = new Style({
      fill: new Fill({ color: '#EEA35A', weight: 4 }),
      stroke: new Stroke({ color: '#EEA35A', width: 4 })
    })

    return this.buildPath(steps, style)
  },

  buildFutureStepsPath (packagePayload) {
    if (!this.hasFuturePath(packagePayload)) {
      return new VectorLayer()
    }
    const steps = this.getFuturePathSteps(packagePayload)

    const style = new Style({
      fill: new Fill({ color: '#FFFFFF', weight: 4 }),
      stroke: new Stroke({ color: '#FFFFFF', width: 4, lineDash: [10, 10] })
    })

    return this.buildPath(steps, style)
  },

  buildPath (steps, style) {
    const coordinates = steps.map(step => [step.info.address.longitude, step.info.address.latitude])

    if (coordinates.length <= 1) {
      return new VectorLayer()
    }

    const bezierLineFeature = bezier({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coordinates }
    })

    let geojsonObject = {
      type: 'FeatureCollection',
      features: [bezierLineFeature]
    }
    let vectorSource = new VectorSource({
      features: (new GeoJSON()).readFeatures(geojsonObject, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
    })
    return new VectorLayer({ source: vectorSource, style })
  },

  addArrowMarker (map, packagePayload) {
    if (!this.hasFuturePath(packagePayload)) {
      return
    }
    const steps = this.getFuturePathSteps(packagePayload)
    const coordinates = steps.map(step => [step.info.address.longitude, step.info.address.latitude])

    const lineFeature = {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates }
    }
    const bezierLineFeature = bezier(lineFeature, { resolution: 100000 })
    const bezierLineCoordinates = bezierLineFeature.geometry.coordinates

    const style = new Style({
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color: 'black' }),
        stroke: new Stroke({ color: 'white', width: 2 })
      })
    })

    let layer = new VectorLayer()
    layer.set('name', 'arrow-marker')
    map.addLayer(layer)

    let animationSpeed = 0.5 // TODO this speed should be relative to the line length
    let animationStartingTime = new Date().getTime()
    moveFeatureCallback = function (event) {
      let elapsedTime = event.frameState.time - animationStartingTime
      let index = Math.round(animationSpeed * elapsedTime % (bezierLineCoordinates.length - 1))

      let currentPoint = new Point(fromLonLat(bezierLineCoordinates[index]))
      let feature = new Feature(currentPoint)
      event.vectorContext.drawFeature(feature, style)

      map.render()
    }
    map.on('postcompose', moveFeatureCallback)
  },

  removeArrowMarker (map) {
    if (moveFeatureCallback) {
      map.un('postcompose', moveFeatureCallback)
    }
  },

  getHubsExtent (packagePayload) {
    let steps
    if (packagePayload.currentStep) {
      steps = [...packagePayload.previousSteps, packagePayload.currentStep, ...packagePayload.futureSteps]
    } else {
      steps = [...packagePayload.previousSteps, ...packagePayload.futureSteps]
    }

    const points = steps
      .filter(step => step.info.type === 'HUB')
      .map(step => [step.info.address.longitude, step.info.address.latitude])

    var ext = boundingExtent(points)
    return transformExtent(ext, get('EPSG:4326'), get('EPSG:3857'))
  },

  zoomAndCenterMapOnExtent (map, extent) {
    let padding
    if (window.innerWidth < 1024) {
      padding = [0, 0, 0, 0]
    } else {
      padding = [0, 0, 0, window.innerWidth / 4]
    }
    map.getView().fit(extent, { size: map.getSize(), duration: 1000, padding })
  },

  setMapCenter (map, coordinates) {
    const point = transform(coordinates, 'EPSG:4326', 'EPSG:3857')
    map.getView().animate({ center: point, duration: 1000 })
  },

  setMapCenterAndZoom (map, coordinates, zoom) {
    const point = transform(coordinates, 'EPSG:4326', 'EPSG:3857')
    map.getView().animate({ center: point, duration: 1000, zoom })
  },

  hasPreviousPath (packagePayload) {
    const isCurrentStepAHub = packagePayload.currentStep &&
      packagePayload.currentStep.info &&
      packagePayload.currentStep.info.type === 'HUB'

    return packagePayload.previousSteps.length >= 2 || (packagePayload.previousSteps.length === 1 && isCurrentStepAHub)
  },

  getPreviousPathSteps (packagePayload) {
    if (packagePayload.currentStep && packagePayload.currentStep.info.type === 'HUB') {
      return [...packagePayload.previousSteps, packagePayload.currentStep]
    } else {
      return packagePayload.previousSteps
    }
  },

  hasFuturePath (packagePayload) {
    const isCurrentStepAHub = packagePayload.currentStep &&
      packagePayload.currentStep.info &&
      packagePayload.currentStep.info.type === 'HUB'

    return packagePayload.futureSteps.length >= 2 ||
        (packagePayload.futureSteps.length === 1 && packagePayload.previousSteps.length >= 1) ||
        (packagePayload.futureSteps.length === 1 && isCurrentStepAHub)
  },

  getFuturePathSteps (packagePayload) {
    if (packagePayload.currentStep && packagePayload.currentStep.info.type === 'HUB') {
      return [packagePayload.currentStep, ...packagePayload.futureSteps]
    } else if (packagePayload.previousSteps.length >= 1) {
      let lastPreviousStep = packagePayload.previousSteps[packagePayload.previousSteps.length - 1]
      return [lastPreviousStep, ...packagePayload.futureSteps]
    } else {
      return packagePayload.futureSteps
    }
  },

  addBounceHubAnimation (hubs, map) {
    map.on('click', (evt) => {
      const feature = map.forEachFeatureAtPixel(evt.pixel, feature => feature)
      this.onHubClick(feature, hubs)
    })
  },

  onHubClick (feature, hubs) {
    let bounceAnimation = new Bounce({
      duration: 800,
      repeat: 3
    })

    if (feature && feature.values_ && feature.values_.step) {
      hubs.animateFeature(feature, bounceAnimation)
    }
  },

  registerPointerMoveEvents (map, callback) {
    map.on('pointermove', callback)
  },

  createMouseHoverCalllback (state, dispatch) {
    return (event) => {
      const feature = state.map.forEachFeatureAtPixel(event.pixel, feature => feature)
      const isHubFeature = this.featureIsHub(feature)

      if (!state.isMouseHoverHub && isHubFeature) {
        dispatch('startHoveringHub', { step: feature.values_.step })
      } else if (state.isMouseHoverHub && !isHubFeature) {
        dispatch('stopHoveringHub')
      }
    }
  },

  createHubPopup (map) {
    let containerElement = document.getElementById('hub-popup-wrapper')
    let popupOverlay = new Overlay({
      id: 'hub-popup',
      element: containerElement,
      autoPan: true,
      autoPanAnimation: {
        duration: 250
      }
    })
    map.addOverlay(popupOverlay)
  },

  addHubPopup (map, step) {
    let lon = step.info.address.longitude
    let lat = step.info.address.latitude
    let coordinates = fromLonLat([lon, lat])

    let popupOverlay = map.getOverlayById('hub-popup')
    popupOverlay.setPosition(coordinates)
  },

  stepCursorPointer (map) {
    map.getTargetElement().style.cursor = 'pointer'
  },

  cleanCursorStyle (map) {
    map.getTargetElement().removeAttribute('style')
  },

  featureIsHub (feature) {
    return feature && feature.values_ && feature.values_.step
  },

  getHubFeature (hubsLayer, step) {
    const features = hubsLayer.getSource().getFeatures()
    let feature = []
    features.forEach(f => {
      if (f.values_.step.info.name === step.info.name) {
        feature = f
      }
    })
    return feature
  }
}
